import { Navigate, useParams } from "react-router";
import type { PlaybookContentGuidePreview } from "../../state/api.types";
import { useDocumentTitle } from "../../utilities/useDocumentTitle";
import { PlaybookBreadcrumb } from "./PlaybookBreadcrumb";
import { PlaybookFilterLayout } from "./PlaybookFilterLayout";
import { PlaybookGuideCard } from "./PlaybookGuideCard";
import { PlaybookSidebarGuides } from "./PlaybookSidebarGuides";
import { useFilteredPlaybookContentOverview } from "./hooks";
import { PlaybookCardSkeleton, SkeletonLine } from "./skeletons";

export const PlaybookSectionPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading } = useFilteredPlaybookContentOverview();

  const section = data?.sections.find((s) => s.slug === slug);
  const guides: PlaybookContentGuidePreview[] = (data?.guides ?? []).filter(
    (g) => g.section === slug,
  );
  const showSkeleton = isLoading && !section;

  useDocumentTitle(section?.title, "Playbook");

  const breadcrumbItems = [
    { label: "Playbook", to: "/playbook" },
    section ? { label: section.title } : null,
  ];

  // TODO: 404 page?
  if (!section && !isLoading) return <Navigate to="/playbook" replace />;

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-1 flex-col gap-7xl">
      <div className="flex flex-col gap-2 pt-16">
        <PlaybookBreadcrumb className="mb-md" items={breadcrumbItems} />
        <h1 className="text-display-md font-semibold tracking-tight text-primary">
          {section ? section.title : <SkeletonLine className="w-2/3" />}
        </h1>
        <p className="text-md text-tertiary">
          {section ? (
            section.tagline
          ) : (
            <SkeletonLine className="w-2/5" subtle />
          )}
        </p>
      </div>
      <PlaybookFilterLayout sidebar={<PlaybookSidebarGuides section={slug} />}>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(19rem,1fr))] gap-xl">
          {showSkeleton
            ? Array.from({ length: 6 }).map((_, i) => (
                <PlaybookCardSkeleton key={i} />
              ))
            : guides.map((guide) => (
                <PlaybookGuideCard key={guide.slug} guide={guide} />
              ))}
        </div>
      </PlaybookFilterLayout>
    </div>
  );
};
